import {
  Component,
  ContentChild,
  EventEmitter,
  HostBinding,
  Input,
  Output,
} from '@angular/core';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { HeadingSize, HeadingType } from '@freelancer/ui/heading';
import { RibbonComponent } from '@freelancer/ui/ribbon';

export enum CardLevel {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  TERTIARY = 'tertiary',
}

export enum CardSize {
  SMALL = 'small',
  MID = 'mid',
}

export enum CardBorderRadius {
  MID = 'mid',
  LARGE = 'large',
}

export interface CardHeaderButton {
  text: string;
  color?: ButtonColor;
  link?: string;
  fragment?: string;
  queryParams?: { [k: string]: any };
  linkActive?: string[] | string;
  linkActiveOptions?: { exact: boolean };
  busy?: boolean;
}

@Component({
  selector: 'fl-card-header-title',
  template: `
    <ng-content></ng-content>
  `,
})
export class CardHeaderTitleComponent {}

@Component({
  selector: 'fl-card-header-right',
  template: `
    <ng-content></ng-content>
  `,
})
export class CardHeaderRightComponent {}

@Component({
  selector: 'fl-card-header-secondary',
  template: `
    <ng-content></ng-content>
  `,
})
export class CardHeaderSecondaryComponent {}

@Component({
  selector: 'fl-card',
  template: `
    <fl-bit class="Card">
      <fl-bit
        class="CardHeader"
        *ngIf="cardHeaderTitle || cardHeaderButton || cardHeaderSecondary"
        [attr.data-size]="size"
      >
        <fl-bit
          class="CardHeaderInner"
          *ngIf="cardHeaderTitle || cardHeaderButton"
          [attr.data-size]="size"
          [attr.data-button-available]="cardHeaderButton && true"
        >
          <fl-heading
            [headingType]="HeadingType.H2"
            [size]="
              size == CardSize.MID ? HeadingSize.XSMALL : HeadingSize.XXSMALL
            "
          >
            <ng-content select="fl-card-header-title"></ng-content>
          </fl-heading>
          <fl-bit
            class="CardHeaderRight"
            *ngIf="cardHeaderRight || cardHeaderButton"
          >
            <fl-button
              *ngIf="cardHeaderButton"
              [color]="
                cardHeaderButton.color
                  ? cardHeaderButton.color
                  : ButtonColor.SECONDARY
              "
              [size]="size == CardSize.MID ? ButtonSize.SMALL : ButtonSize.MINI"
              [link]="cardHeaderButton.link"
              [linkActive]="cardHeaderButton.linkActive"
              [fragment]="cardHeaderButton.fragment"
              [queryParams]="cardHeaderButton.queryParams"
              [busy]="cardHeaderButton.busy"
              [linkActiveOptions]="cardHeaderButton.linkActiveOptions"
              (click)="handleButtonClick()"
            >
              {{ cardHeaderButton.text }}
            </fl-button>
            <ng-content select="fl-card-header-right"></ng-content>
          </fl-bit>
        </fl-bit>
        <ng-content select="fl-card-header-secondary"></ng-content>
      </fl-bit>
      <fl-bit
        class="CardBody"
        [attr.data-size]="size"
        [attr.data-edge-to-edge]="edgeToEdge"
        [attr.data-ribbon]="ribbon ? true : false"
      >
        <ng-content></ng-content>
      </fl-bit>
    </fl-bit>
  `,
  styleUrls: ['./card.component.scss'],
})
export class CardComponent {
  /** Forces the card to have a full 100% width of its parent container */
  @HostBinding('class.MaxContent')
  @Input()
  maxContent = true;

  /** Defines the level of the box shadow of the card */
  @HostBinding('attr.data-level')
  @Input()
  level = CardLevel.PRIMARY;

  /** Makes the card appear to be clickable */
  @HostBinding('class.CardClickable')
  @Input()
  clickable = false;

  @HostBinding('attr.data-radius')
  @Input()
  borderRadius = CardBorderRadius.MID;

  @Input() cardHeaderButton?: CardHeaderButton;

  /** Removes padding inside the card component */
  @Input() edgeToEdge = false;

  /** Defines the padding level inside the card */
  @Input() size = CardSize.MID;

  @Output() buttonClicked = new EventEmitter<void>();

  @ContentChild(CardHeaderTitleComponent)
  cardHeaderTitle: CardHeaderTitleComponent;
  @ContentChild(CardHeaderRightComponent)
  cardHeaderRight: CardHeaderRightComponent;
  @ContentChild(CardHeaderSecondaryComponent)
  cardHeaderSecondary: CardHeaderSecondaryComponent;
  @ContentChild(RibbonComponent) ribbon: RibbonComponent;

  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  CardSize = CardSize;
  HeadingSize = HeadingSize;
  HeadingType = HeadingType;

  handleButtonClick() {
    this.buttonClicked.emit();
  }
}
